import { z } from "zod"

import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const laborEntryV1Schema = z.object({
  laborEntryId: entityIdSchema,
  workerId: entityIdSchema,
  minutes: z.number().int().positive().max(24 * 60),
  costAmountMinor: z.number().int().nonnegative(),
}).strict()

const vendorCostV1Schema = z.object({
  vendorCostId: entityIdSchema,
  supplierMasterId: entityIdSchema,
  amountMinor: z.number().int().positive(),
  invoiceReference: z.string().trim().min(3).max(140),
}).strict()

export const fleetMaintenanceWorkCompleteV1Schema = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  workItemId: entityIdSchema,
  workRevisionId: entityIdSchema,
  assetId: entityIdSchema,
  repairFollowUpId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  expectedVersion: z.literal(0),
  completedAt: z.iso.datetime({ offset: true }),
  downtimeStartedAt: z.iso.datetime({ offset: true }),
  downtimeEndedAt: z.iso.datetime({ offset: true }),
  repairSummary: z.string().trim().min(10).max(2_000),
  repairEvidenceRefs: z.array(z.string().trim().min(3).max(256)).min(1).max(32),
  partMovementIds: z.array(entityIdSchema).min(1).max(32),
  labor: z.array(laborEntryV1Schema).min(1).max(64),
  vendorCosts: z.array(vendorCostV1Schema).max(32),
  currency: z.string().regex(/^[A-Z]{3}$/),
}).strict().superRefine((value, context) => {
  if (Date.parse(value.downtimeEndedAt) <= Date.parse(value.downtimeStartedAt)
    || Date.parse(value.completedAt) < Date.parse(value.downtimeEndedAt)) {
    context.addIssue({ code: "custom", message: "Maintenance work times are invalid." })
  }
  for (const values of [
    value.repairEvidenceRefs,
    value.partMovementIds,
    value.labor.map((entry) => entry.laborEntryId),
    value.vendorCosts.map((entry) => entry.vendorCostId),
  ]) {
    if (new Set(values).size !== values.length) {
      context.addIssue({ code: "custom", message: "Maintenance work entries must be unique." })
    }
  }
})

export const fleetMaintenanceWorkResultV1Schema = z.object({
  entityType: z.literal("fleet_maintenance_work"),
  entityId: entityIdSchema,
  state: z.literal("completed"),
  version: z.literal(1),
  assetId: entityIdSchema,
  repairFollowUpId: entityIdSchema,
  partCostAmountMinor: z.number().int().nonnegative(),
  laborCostAmountMinor: z.number().int().nonnegative(),
  vendorCostAmountMinor: z.number().int().nonnegative(),
  totalCostAmountMinor: z.number().int().nonnegative(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  downtimeMinutes: z.number().int().positive(),
  releaseRequired: z.literal(true),
  operatingStatus: z.literal("Out of Service"),
}).strict()

export const fleetReleaseAuthorizeV1Schema = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  releaseId: entityIdSchema,
  workItemId: entityIdSchema,
  assetId: entityIdSchema,
  assetRevisionId: entityIdSchema,
  statusEventId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  expectedVersion: z.number().int().positive(),
  workExpectedVersion: z.literal(1),
  releasedAt: z.iso.datetime({ offset: true }),
  reason: z.string().trim().min(10).max(512),
  evidenceReviewed: z.literal(true),
}).strict()

export const fleetReleaseResultV1Schema = z.object({
  entityType: z.literal("fleet_asset"),
  entityId: entityIdSchema,
  state: z.literal("Available"),
  version: z.number().int().positive(),
  releaseId: entityIdSchema,
  workItemId: entityIdSchema,
  operatingStatus: z.literal("Available"),
  availability: z.literal("available"),
  independentRelease: z.literal(true),
}).strict()

export type FleetMaintenanceWorkCompleteV1 =
  z.infer<typeof fleetMaintenanceWorkCompleteV1Schema>
export type FleetMaintenanceWorkResultV1 =
  z.infer<typeof fleetMaintenanceWorkResultV1Schema>
export type FleetReleaseAuthorizeV1 = z.infer<typeof fleetReleaseAuthorizeV1Schema>
export type FleetReleaseResultV1 = z.infer<typeof fleetReleaseResultV1Schema>
